import * as React from "react"
import {
  FaArrowRight,
  FaAward,
  FaBars,
  FaCalendarCheck,
  FaChevronDown,
  FaClock,
  FaComments,
  FaEnvelope,
  FaFolderOpen,
  FaHardHat,
  FaImages,
  FaList,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaStar,
} from "react-icons/fa"
import TradeIcon from "../TradeIcon"
import QuoteForm from "./QuoteForm"
import { buildView } from "./view-model"
import "../../../styles/templates/base.css"
import "../../../styles/templates/v3.css"
import "../../../styles/templates/v3.mobile.css"
import "../../../styles/templates/port.css"

/**
 * Free Template 2 — the Mockup Maker's v3 design, ported — MTC-FCS-041.
 * ============================================================================
 * Same DOM and same stylesheet as `source--mockup-maker/src/templates/v3`,
 * rendered from this builder's draft. v3.css is copied verbatim; only the data
 * plumbing is new.
 *
 * ── WHAT IS MISSING IS MISSING, NOT INVENTED ───────────────────────────────
 * The original shows review scores, a credential badge and opening hours when
 * the rep has typed them. This builder does not collect any of those, so every
 * element that would state one is left out — the hero drops to its `is-solo`
 * layout, the trust strip carries only the two claims that are true of every
 * job, and the ratings block does not render at all.
 */

/**
 * ── LOCAL OR REMOTE — FCS-057(b) ───────────────────────────────────────────
 * DEFAULT_MODE is the switch this design runs in when nobody says otherwise,
 * and it is "local": the content is whatever the builder hands over, which is
 * the draft somebody is editing.
 *
 * In "remote" mode the same component draws a PUBLISHED site, and the JSON
 * fetched from Storage is the only source of anything — text, photographs,
 * contact details, colours. Nothing here reaches for a sample value in that
 * mode, because a placeholder on a real contractor's domain is a sentence
 * they never wrote on a website customers are reading. The refusing is done
 * before this point, in lib/site-content.js: a file that is missing a
 * required field never reaches a template at all.
 *
 * TEMPLATE_VERSION is what a published file is checked against, so a site
 * published against an older shape of this design is refused rather than
 * rendered wrong.
 */
export const DEFAULT_MODE = "local"
export const TEMPLATE_ID = "mm-v3"
export const TEMPLATE_VERSION = "1"

const Photo = ({ src, slot, alt }) =>
  src ? (
    <div className="mk-photo" data-slot={slot}>
      <img src={src} alt={alt || ""} />
    </div>
  ) : (
    <div className="mk-photo" data-slot={slot} aria-hidden="true" />
  )

const TemplateV3 = ({ draft, plan, mode = DEFAULT_MODE }) => {
  const v = buildView(draft, { plan, mode })
  const showTopbar = v.hasPhone || v.hasEmail

  const navItems = [
    { href: "#top", label: "Home" },
    { href: "#services", label: "Services" },
    ...(v.hasGallery ? [{ href: "#gallery", label: "Our Work" }] : []),
    { href: "#about", label: "About" },
    { href: "#contact", label: "Contact" },
  ]

  return (
    <div className="mk mk-v3" style={v.style} data-mode="light" id="top">
      {/* ── HEADER ─────────────────────────────── */}
      <header className={`v3-header${showTopbar ? "" : " is-bare"}`}>
        {showTopbar && (
          <div className="v3-topbar">
            <div className="v3-container v3-topbar-inner">
              <div className="v3-topbar-left">
                {v.hasPhone && (
                  <span className="v3-topbar-link">
                    <FaPhoneAlt /> {v.phone}
                  </span>
                )}
                {v.hasPhone && v.hasEmail && <span className="v3-topbar-divider">|</span>}
                {v.hasEmail && (
                  <span className="v3-topbar-link">
                    <FaEnvelope /> {v.email}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="v3-container">
          <div className="v3-nav-wrap">
            <div className="v3-logo">
              <span className="mk-monogram">{v.initials}</span>
              <span className="v3-logo-text">
                {v.lead} {v.accent && <em>{v.accent}</em>}
              </span>
            </div>

            <ul className="v3-nav-list">
              {navItems.map((link, i) => (
                <li key={link.label}>
                  <a href={link.href} className={`v3-nav-link${i === 0 ? " is-active" : ""}`}>
                    {link.label}
                    {link.label === "Services" && <FaChevronDown />}
                  </a>
                </li>
              ))}
            </ul>

            <a href="#contact" className="v3-nav-cta">
              Get a Quote
              <span className="v3-nav-cta-icon">
                <FaArrowRight />
              </span>
            </a>

            <span className="v3-burger">
              <FaBars />
            </span>
          </div>
        </div>
      </header>

      {/* ── HERO ───────────────────────────────── */}
      <section className="v3-hero">
        <Photo src={v.heroImage} slot="hero" />
        <div className="v3-hero-overlay" />

        <div className="v3-hero-inner is-solo">
          <div>
            <h1 className="v3-hero-title">
              {v.trade} {v.cityIsReal && <em>Across {v.city}</em>}
            </h1>

            {v.aboutShort && <p className="v3-hero-desc">{v.aboutShort}</p>}
          </div>
        </div>

        <div className="v3-bottom-bar">
          <div className="v3-bottom-wrap">
            <nav className="v3-bottom-nav">
              <a href="#services" className="v3-bottom-link">
                <FaList /> Services
              </a>
              {v.hasGallery && (
                <a href="#gallery" className="v3-bottom-link">
                  <FaFolderOpen /> Past Projects
                </a>
              )}
              <a href="#contact" className="v3-bottom-link">
                <FaComments /> Request Quote
              </a>
            </nav>
            <a href="#contact" className="v3-bottom-cta">
              Get A Free Quote
            </a>
          </div>
        </div>
      </section>

      {/* ── ABOUT ──────────────────────────────── */}
      <section className="v3-about" id="about">
        <div className="v3-container v3-about-grid">
          <div>
            <span className="v3-tag">
              <FaAward /> About {v.name}
            </span>

            <h2 className="v3-about-title">{v.name}</h2>

            {v.aboutText && <p className="v3-about-desc">{v.aboutText}</p>}

            <div className="v3-highlights">
              <span className="v3-highlight">
                <FaHardHat /> {v.services.map(s => s.title).join(" • ")}
              </span>
              {v.serviceArea && (
                <span className="v3-highlight">
                  <FaMapMarkerAlt /> Serving {v.serviceArea}
                </span>
              )}
            </div>

            <a href="#contact" className="v3-about-cta">
              Get Your Free Quote <FaArrowRight />
            </a>
          </div>

          <div className="v3-about-image">
            <div className="v3-image-wrapper">
              <Photo src={v.heroImage} slot="hero" />
            </div>
          </div>
        </div>
      </section>

      {/* ── SERVICES ───────────────────────────── */}
      <section className="v3-services" id="services">
        <div className="v3-container">
          <div className="v3-services-head">
            <span className="v3-tag">
              <FaHardHat /> Our Services
            </span>
            <h2 className="v3-services-title">What We Do</h2>
            <p className="v3-services-sub">
              {v.serviceArea ? `Serving ${v.serviceArea}.` : ""}
            </p>
          </div>

          {/* "Trained Crews: no subcontractors" and "Rapid Response: same-week bookings" were drawn here for every contractor. They are
              claims, and a promise about timing, and nobody told us either. (FCS-124) */}

          <div className="v3-services-grid">
            {v.services.map((service, i) => (
              <article className="v3-card" key={service.slug}>
                <div className="v3-card-media">
                  <Photo src={service.image} slot={`service${i + 1}`} />
                  {!service.image && (
                    <span className="v3-card-glyph">
                      <TradeIcon name={service.icon} />
                    </span>
                  )}
                  <div className="v3-card-media-veil" />
                  <span className="v3-card-icon">
                    <TradeIcon name={service.icon} />
                  </span>
                </div>

                <div className="v3-card-body">
                  <h3 className="v3-card-title">{service.title}</h3>
                  <p className="v3-card-desc">{service.description}</p>
                  <div className="v3-card-actions">
                    <a href="#contact" className="v3-card-btn">
                      Learn More <FaArrowRight />
                    </a>
                    {v.hasPhone && (
                      <a href={v.phoneHref} className="v3-card-call" aria-label={`Call ${v.phone}`}>
                        <FaPhoneAlt />
                      </a>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="v3-cta-bar">
            <div className="v3-cta-bar-text">
              <h3>Not sure which service you need?</h3>
              <p>
                Get in touch and tell us what you need.
              </p>
            </div>
            <div className="v3-cta-bar-actions">
              {v.hasPhone && (
                <a href={v.phoneHref} className="v3-cta-bar-primary">
                  <FaPhoneAlt /> Call {v.phone}
                </a>
              )}
              <a href="#contact" className="v3-cta-bar-secondary">
                Request Free Quote <FaArrowRight />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── OUR WORK ───────────────────────────── */}
      {v.hasGallery && (
        <section className="v3-work" id="gallery">
          <div className="v3-container">
            <div className="v3-services-head">
              <span className="v3-tag">
                <FaImages /> Our Work
              </span>
              <h2 className="v3-services-title">Recent Projects</h2>
            </div>
            <div className="v3-work-grid">
              {v.gallery.slice(0, 6).map((url, i) => (
                <figure className="v3-work-tile mk-tile" key={`${url}-${i}`}>
                  <Photo src={url} slot={`gallery${i + 1}`} />
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CLOSING CALL TO ACTION ─────────────── */}
      <section className="v3-cta" id="contact">
        <Photo />
        <div className="v3-cta-overlay" />

        <div className="v3-cta-inner">
          <div className="v3-cta-content">
            <span className="v3-cta-badge">Get Your Project Moving</span>
            <h2 className="v3-cta-title">
              <em>
                {v.cityIsReal ? `${v.city} ` : ""}
                {v.trade}
              </em>
            </h2>
            <p className="v3-cta-sub">Get in touch for a quote.</p>
            <div className="v3-cta-actions">
              {v.hasPhone && (
                <a href={v.phoneHref} className="v3-cta-btn-primary">
                  <FaPhoneAlt /> Call {v.phone}
                </a>
              )}
              {v.hasEmail && (
                <a href={v.emailHref} className="v3-cta-btn-secondary">
                  <FaCalendarCheck /> Email us
                </a>
              )}
            </div>
          </div>

          {/* "Trained Crews: every person on site is trained, experienced and directly employed by us" and "Honest Pricing: itemised
              quotes with no hidden extras" were drawn here for every contractor. Nobody told us either, so neither is said. (FCS-124) */}
        </div>
      </section>


      {/* ── REQUEST A QUOTE — MTC-FCS-017 ──────── */}
      {v.wantsForm && (
        <section className="mk-quote-section" id="quote" data-premium="leadform">
          <div className="mk-quote-inner">
            <div className="mk-quote-head">
              <h2 className="v3-cta-title">Request a Quote</h2>
              <p className="mk-quote-lead">
                Tell us what you need and we will come back to you.
              </p>
            </div>
            <QuoteForm view={v} />
          </div>
        </section>
      )}
      {/* ── FOOTER ─────────────────────────────── */}
      <footer className="v3-footer">
        <div className="v3-container">
          <div className="v3-footer-grid">
            <div>
              <div className="v3-footer-logo">
                {v.lead} {v.accent && <em>{v.accent}</em>}
              </div>
              {v.aboutShort && <p className="v3-footer-desc">{v.aboutShort}</p>}
            </div>

            <div>
              <h3 className="v3-footer-title">Quick Links</h3>
              <ul className="v3-footer-links">
                {navItems.map(link => (
                  <li key={link.label}>
                    <a href={link.href}>{link.label}</a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="v3-footer-title">Our Services</h3>
              <ul className="v3-footer-links">
                {v.services.map(service => (
                  <li key={service.slug}>
                    <a href="#services">{service.title}</a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="v3-footer-title">Connect With Us</h3>
              <div className="v3-footer-social">
                {v.serviceArea && (
                  <span>
                    <FaMapMarkerAlt /> {v.serviceArea}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="v3-footer-bottom">
            <p>
              © {v.year} {v.name}. All rights reserved.
            </p>
            {(v.hasPhone || v.hasEmail) && (
              <div className="v3-footer-contact">
                {v.hasPhone && (
                  <a href={v.phoneHref}>
                    <FaPhoneAlt /> {v.phone}
                  </a>
                )}
                {v.hasEmail && (
                  <a href={v.emailHref}>
                    <FaEnvelope /> {v.email}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </footer>
    </div>
  )
}

export default TemplateV3
